import { Component, inject, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NewsService } from '../../../shared/services/news/news.service';
import { MatButtonModule } from '@angular/material/button';
import { MatSortModule } from '@angular/material/sort';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { UploadFilesComponent } from '../../../shared/components/upload-files/upload-files.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  provideNativeDateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { QuillModule } from 'ngx-quill';
import { QuillConfigService } from '../../../shared/services/quill-config.service';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { RolesService } from '../../../shared/services/roles/roles.service';

// Define custom date formats
export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-add-activities',
  imports: [
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatSortModule,
    MatCheckboxModule,
    UploadFilesComponent,
    MatDatepickerModule,
    FormsModule,
    RouterLink,
    CommonModule,
    QuillModule,
    LoaderComponent,
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
  ],
  templateUrl: './add-activities.component.html',
  styleUrls: [
    './add-activities.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class AddActivitiesComponent {
  modules: any;
  private _snackBar = inject(MatSnackBar);
  myroles: any = {};

  element: any = null;

  // Replace individual form controls with a FormGroup
  activityForm: FormGroup;

  // Add selectedFiles property
  selectedFiles: File[] = [];

  // Add selectedImage property if not already present
  selectedImage: File | null = null;

  @ViewChild(UploadFilesComponent) uploadFilesComponent!: UploadFilesComponent;

  constructor(
    private newsService: NewsService,
    private router: Router,
    private fb: FormBuilder,
    private loaderService: LoaderService,
    private quillConfigService: QuillConfigService,
    private storageService: StorageService,
    private rolesService: RolesService
  ) {
    this.modules = this.quillConfigService.getDefaultModules();

    // Initialize form using FormBuilder
    this.activityForm = this.fb.group({
      title: ['', [Validators.required]],
      description: ['', [Validators.required]],
      tags: [''],
      featured: [false],
      notifications: [false],
      isProgrammed: [false],
      dateValue: [null],
      timeValue: ['00:00'],
      endTimeValue: ['23:59'],
      location: [''],
      notification_management: [true],
      notification_drivers: [true],
      allow_comments: [false],
      allow_reactions: [false],
      allow_images: [false],
      allow_images_direct: [false],
      registration_link: [
        '',
        [
          // Custom validator that only validates if the field is not empty
          (control: AbstractControl) => {
            if (!control.value || control.value.trim() === '') {
              return null; // No validation error for empty values
            }

            const value = control.value.trim();

            // More comprehensive URL pattern that accepts various formats
            const urlPattern =
              /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?(\?[;&a-z\d%_\.,~#=]*)?(\#[-a-z\d_]*)?$/i;

            // Also accept URLs without protocol
            const simplePattern = /^[\w\.-]+\.[\w]{2,}(\/.*)?$/i;

            return urlPattern.test(value) || simplePattern.test(value)
              ? null
              : { pattern: true };
          },
        ],
      ],
    });
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  event: any = {
    event_days: [],
  };

  // Use getters for form controls to simplify template access
  get isProgrammed() {
    return this.activityForm.get('isProgrammed')?.value;
  }

  set isProgrammed(value: boolean) {
    this.activityForm.get('isProgrammed')?.setValue(value);
  }

  get notifications() {
    return this.activityForm.get('notifications')?.value;
  }

  set notifications(value: boolean) {
    this.activityForm.get('notifications')?.setValue(value);
  }

  get notification_management() {
    return this.activityForm.get('notification_management')?.value;
  }

  set notification_management(value: boolean) {
    this.activityForm.get('notification_management')?.setValue(value);
  }

  get notification_drivers() {
    return this.activityForm.get('notification_drivers')?.value;
  }

  set notification_drivers(value: boolean) {
    this.activityForm.get('notification_drivers')?.setValue(value);
  }

  get allow_reactions() {
    return this.activityForm.get('allow_reactions')?.value;
  }

  set allow_reactions(value: boolean) {
    this.activityForm.get('allow_reactions')?.setValue(value);
  }

  get allow_images() {
    return this.activityForm.get('allow_images')?.value;
  }
  
  set allow_images(value: boolean) {
    this.activityForm.get('allow_images')?.setValue(value);
  }

  get allow_comments() {
    return this.activityForm.get('allow_comments')?.value;
  }

  set allow_comments(value: boolean) {
    this.activityForm.get('allow_comments')?.setValue(value);
  }

  get allow_images_direct() {
    return this.activityForm.get('allow_images_direct')?.value;
  }
  
  set allow_images_direct(value: boolean) {
    this.activityForm.get('allow_images_direct')?.setValue(value);
  }

  get featured() {
    return this.activityForm.get('featured')?.value;
  }

  set featured(value: boolean) {
    this.activityForm.get('featured')?.setValue(value);
  }

  get dateValue() {
    return this.activityForm.get('dateValue')?.value;
  }

  set dateValue(value: Date | null) {
    this.activityForm.get('dateValue')?.setValue(value);
  }

  get timeValue() {
    return this.activityForm.get('timeValue')?.value;
  }

  set timeValue(value: string) {
    this.activityForm.get('timeValue')?.setValue(value);
  }

  // Add property to track if validation has been attempted
  validationAttempted = false;

  // Check if at least one date is selected
  get hasMinimumDates(): boolean {
    return this.event.event_days && this.event.event_days.length > 0;
  }

  // Show date error when validation is attempted and no dates exist
  get showDateError(): boolean {
    return this.validationAttempted && !this.hasMinimumDates;
  }

  // Función para mostrar un snackbar
  openSnackBar(message: string) {
    this._snackBar.open(message, 'Cerrar', {
      duration: 5000,
    });
  }

  getFormattedDateTime(): string {
    if (!this.dateValue) return 'Fecha no seleccionada';

    return `${this.dateValue.toLocaleDateString()} ${this.timeValue}`;
  }

  // When you need the combined datetime value (e.g., for saving)
  getCombinedDateTime(): Date | null {
    if (!this.dateValue) return null;
    const base = this.dateValue;
    const parts = this.timeValue.split(':');
    const hh = parseInt(parts[0] || '0', 10);
    const mm = parseInt(parts[1] || '0', 10);
    const ss = parseInt(parts[2] || '0', 10);
    const local = new Date(
      base.getFullYear(),
      base.getMonth(),
      base.getDate(),
      isNaN(hh) ? 0 : hh,
      isNaN(mm) ? 0 : mm,
      isNaN(ss) ? 0 : ss,
      0
    );
    return local; // toISOString() converts to real UTC
  }

  // Update onFileSelected method
  onFileSelected(files: File[]): void {
    if (files && files.length > 0) {
      this.selectedImage = files[0];
      this.selectedFiles = files;
    }
  }

  saveChanges() {
    this.loaderService.showLoader();

    // Set validation attempted flag
    this.validationAttempted = true;

    // Check if at least one date is provided
    if (!this.event.event_days || this.event.event_days.length === 0) {
      this.loaderService.hideLoader();
      this.openSnackBar(
        'Debe seleccionar al menos una fecha para la actividad'
      );
      return;
    }

    if (this.activityForm.invalid) {
      this.loaderService.hideLoader();
      // Mark all controls as touched to trigger validation visuals
      Object.keys(this.activityForm.controls).forEach((key) => {
        const control = this.activityForm.get(key);
        control?.markAsTouched();
      });

      // Check specifically for URL format issues
      if (this.activityForm.get('registration_link')?.hasError('pattern')) {
        this.openSnackBar('Error, formato incorrecto en el enlace');
      } else {
        this.openSnackBar('Por favor complete todos los campos requeridos');
      }
      return;
    }

    // Create a FormData object instead of a plain JavaScript object
    const formData = new FormData();
    const formValues = this.activityForm.value;

    // Use values from the first date [0], even if there are multiple days
    const firstDate = this.event.event_days[0];
    formData.append('event_date', firstDate.date);
    formData.append('start_time', firstDate.start_time);
    formData.append('end_time', firstDate.end_time);
    formData.append('title', formValues.title || '');
    // Preserve emojis (including trailing ones) by preferring the Quill editor DOM HTML
    let description = formValues.description;
    try {
      const editor = document.querySelector('.ql-editor');
      const html = editor?.innerHTML;
      if (html && html.trim() !== '') {
        description = html;
      }
    } catch (error) {
      // ignore - fallback to form value
    }
    formData.append('description', description || '');
    formData.append('location', formValues.location || '');

    // Only append registration_link if it's not empty and is a valid URL
    if (
      formValues.registration_link &&
      formValues.registration_link.trim() !== ''
    ) {
      let url = formValues.registration_link.trim();
      // Ensure URL has a protocol
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      formData.append('registration_link', url);
    }

    // Add tags
    if (formValues.tags) {
      formData.append(
        'tags_string',
        formValues.tags
          .split(',')
          .map((tag: string) => tag.trim())
          .join(', ')
      );
    }

    // Add flags - published should be true if not programmed, false if programmed
    formData.append('published', formValues.isProgrammed ? 'false' : 'true');
    formData.append(
      'scheduled_publication',
      formValues.isProgrammed ? 'true' : 'false'
    );
    formData.append('notifications', formValues.notifications.toString());
    formData.append('notification_management', formValues.notification_management.toString());
    formData.append('notification_drivers', formValues.notification_drivers.toString());
    formData.append('allow_comments', formValues.allow_comments.toString());
    formData.append('allow_reactions',formValues.allow_reactions.toString());
    formData.append('allow_images',formValues.allow_images.toString());
    formData.append('featured', formValues.featured.toString());
    formData.append('allow_images_direct',formValues.allow_images_direct.toString());

    // Add publication date if programmed
    if (formValues.isProgrammed && this.getCombinedDateTime()) {
      formData.append(
        'publication_date',
        this.getCombinedDateTime()?.toISOString() || ''
      );
    }

    // Handle event_days differently - we need to add each one individually
    if (this.event.event_days && this.event.event_days.length > 0) {
      this.event.event_days.forEach((day: any, index: number) => {
        // console.log(`Processing event day ${index}:`, day);

        let formattedDate = day.date;
        if (day.date instanceof Date) {
          // Format the date manually to preserve the selected day
          const year = day.date.getFullYear();
          const month = (day.date.getMonth() + 1).toString().padStart(2, '0');
          const dayOfMonth = day.date.getDate().toString().padStart(2, '0');
          formattedDate = `${year}-${month}-${dayOfMonth}`;
          // console.log(`Converted Date object to string: ${formattedDate}`);
        } else {
          // console.log(`Using existing date string: ${formattedDate}`);
        }

        // console.log(
        //   `Adding to FormData: event_days[${index}][date] = ${formattedDate}`
        // );
        formData.append(`event_days[${index}][date]`, formattedDate);
        formData.append(`event_days[${index}][start_time]`, day.start_time);
        formData.append(`event_days[${index}][end_time]`, day.end_time);
      });
    }

    // Add image file if available
    if (this.selectedFiles.length > 0) {
      formData.append('image', this.selectedFiles[0]);
    }

    // Log the form data for debugging (it will just show "FormData" in console)
    // console.log('Sending FormData:', formData);

    // More detailed logging
    // console.log('FormData entries:');
    // formData.forEach((value, key) => {
    //   console.log(`${key}: ${value instanceof File ? value.name : value}`);
    // });

    // Log all event days for debugging
    // console.log(
    //   'All event days:',
    //   this.event.event_days.map((day: any) => ({
    //     id: day.id,
    //     date: day.date,
    //     start_time: day.start_time,
    //     end_time: day.end_time,
    //   }))
    // );

    this.newsService.postActivities(formData).subscribe({
      next: (response) => {
        this.loaderService.hideLoader();
        this.openSnackBar('Actividad creada con éxito.');
        this.router.navigate(['/home/communication/activities']);
      },
      error: (error) => {
        this.loaderService.hideLoader();
        console.error('Error:', error);

        // Check for specific URL format error in the response
        if (
          error.error &&
          error.error.registration_link &&
          error.error.registration_link.includes('Enter a valid URL.')
        ) {
          this.openSnackBar('Error, formato incorrecto en el enlace');
        } else {
          this.openSnackBar('Error al crear la actividad. Inténtalo de nuevo.');
        }
      },
    });
  }

  activityDates: Date[] = [new Date()];

  // Método para eliminar una fecha
  removeDate(index: number): void {
    if (this.activityDates.length > 1) {
      this.activityDates.splice(index, 1);
    }
  }

  // Handle time changes for start and end times
  onTimeChange(day: any, type: 'start' | 'end'): void {
    // Ensure time format is correct (HH:MM:SS)
    if (type === 'start') {
      // console.log(`Start time before formatting: ${day.start_time}`);
      // Make sure it has seconds
      if (!day.start_time.includes(':')) {
        day.start_time += ':00';
      } else if (day.start_time.split(':').length === 2) {
        day.start_time += ':00';
      }
      // console.log(`Start time changed for day ${day.id} to ${day.start_time}`);
    } else {
      // console.log(`End time before formatting: ${day.end_time}`);
      // Make sure it has seconds
      if (!day.end_time.includes(':')) {
        day.end_time += ':00';
      } else if (day.end_time.split(':').length === 2) {
        day.end_time += ':00';
      }
      // console.log(`End time changed for day ${day.id} to ${day.end_time}`);
    }
  }

  // Handle date changes from datepicker
  onDateChange(day: any, event: any): void {
    // Original date object from the datepicker
    const selectedDate = event.value;
    // console.log(`Raw selected date from datepicker:`, selectedDate);

    if (selectedDate) {
      // Log the date components to see what's happening
      // console.log(`Date components:`, {
      //   year: selectedDate.getFullYear(),
      //   month: selectedDate.getMonth() + 1, // 0-based, so add 1
      //   day: selectedDate.getDate(),
      //   hours: selectedDate.getHours(),
      //   minutes: selectedDate.getMinutes(),
      //   timezone: selectedDate.getTimezoneOffset(),
      // });

      // Format the date manually to preserve the selected day without timezone issues
      const year = selectedDate.getFullYear();
      const month = (selectedDate.getMonth() + 1).toString().padStart(2, '0');
      const dayOfMonth = selectedDate.getDate().toString().padStart(2, '0');

      // Create YYYY-MM-DD format manually
      const formattedDate = `${year}-${month}-${dayOfMonth}`;

      // Update the day.date with our manually formatted date
      day.date = formattedDate;

      // console.log(
      //   `Date changed for day ${day.id} to ${day.date} (manually formatted)`
      // );
    }
  }

  // Update your addNewDate method to add to event_days instead
  addNewDate() {
    if (!this.event.event_days) {
      this.event.event_days = [];
    }

    // Create a new event day with default values
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');

    const newDay: any = {
      id: this.getNextEventDayId(),
      date: `${year}-${month}-${day}`, // Today's date in YYYY-MM-DD format
      start_time: '09:00:00',
      end_time: '17:00:00',
    };

    this.event.event_days.push(newDay);

    // Reset validation state when user adds a date
    this.validationAttempted = false;
  }

  // Remove an event day by ID without checking length
  removeEventDay(id: number) {
    if (this.event.event_days) {
      const index = this.event.event_days.findIndex(
        (day: any) => day.id === id
      );
      if (index !== -1) {
        this.event.event_days.splice(index, 1);

        // Reset validation state when user removes a date
        this.validationAttempted = false;
      }
    }
  }

  // Helper method to generate a unique ID for new event days
  private getNextEventDayId(): number {
    if (!this.event.event_days || this.event.event_days.length === 0) {
      return 1;
    }

    // Find the maximum ID and add 1
    const maxId = Math.max(...this.event.event_days.map((day: any) => day.id));
    return maxId + 1;
  }

  loadRoles() {
    // Get permissions using the service method
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'activities',
      this.storageService
    );

    if (permissions) {
      // Store permissions in myroles
      this.myroles = permissions;

      // Check write permission and redirect if needed
      this.rolesService.checkWritePermission(
        'activities',
        this.storageService,
        this._snackBar,
        this.router,
        '/home/communication/activities',
        'No tienes permisos para añadir una actividad'
      );
    } else {
      // console.log('No user roles found in storage');
    }
  }

  ngOnInit() {
    this.loadRoles();

    // Initialize with one default date
    if (!this.event.event_days || this.event.event_days.length === 0) {
      this.addNewDate();
    }
  }
}
